import cx from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { useMapZoom } from './useMapZoom';
import { useMaxPinsPerCluster } from './useMaxPinsPerCluster';
import { IPinsLayer } from './usePinsLayer';
import * as styles from './usePinsVisuals.css';
import { TFeature } from '../../../../common/types/map';
import { IApplicationState } from '../../../../common/types/redux';
import { expandClusterPinFeature } from '../../../../common/utils/features';
import { selectActiveFeature } from '../../../selectors';

interface IUsePinsVisualsParams extends IPinsLayer {
  ymaps: YMaps.IYMaps;
  map: YMaps.Map;
  isResultsAvailable: boolean;
  isDetailsVisible: boolean;
}

interface IActiveIconLayoutParams {
  isSmall: boolean;
}

export function usePinsVisuals({ ymaps, map, isResultsAvailable, isDetailsVisible }: IUsePinsVisualsParams): void {
  const selectedFeature = useSelector<IApplicationState, TFeature | null>(selectActiveFeature);
  const mapZoom = useMapZoom(map);
  const maxPinsPerCluster = useMaxPinsPerCluster();

  const activeIconLayout = React.useRef(({ isSmall }: IActiveIconLayoutParams) =>
    ymaps.templateLayoutFactory.createClass(
      `<div class="${cx(
        styles['dailyrent-container'],
        isSmall && styles['container--small'],
        styles['container--blue'],
      )}">{% if properties.count > 1 %}{% if properties.count <= 9 %}{{properties.count}}{% else %}9+{% endif %}{% endif %}</div>`,
    ),
  );

  const activePlacemarkRef = React.useRef<YMaps.GeoObject | null>(null);

  React.useEffect(() => {
    if (activePlacemarkRef.current) {
      map.geoObjects.remove(activePlacemarkRef.current);
      activePlacemarkRef.current = null;
    }

    if (!isResultsAvailable || (isDetailsVisible && !selectedFeature)) {
      return;
    }

    if (selectedFeature) {
      // снапшот selectedFeature хранит координаты пина на момент клика —
      // позицию пересчитываем от центра кластера под текущий зум,
      // иначе при зуме точка зависнет на старом месте
      const expandedFeature = expandClusterPinFeature(selectedFeature, mapZoom, maxPinsPerCluster);

      if (!expandedFeature) {
        return;
      }

      const isSmallPlacemark = selectedFeature.properties.offers.length === 1;

      activePlacemarkRef.current = new ymaps.GeoObject(
        {
          ...expandedFeature,
          type: 'Feature',
          id: 'DotActiveFeature',
        },
        {
          iconLayout: activeIconLayout.current({ isSmall: isSmallPlacemark }),
        },
      );

      map.geoObjects.add(activePlacemarkRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature, mapZoom, maxPinsPerCluster]);

  React.useEffect(() => {
    if (!isResultsAvailable || isDetailsVisible) {
      if (activePlacemarkRef.current) {
        map.geoObjects.remove(activePlacemarkRef.current);
        activePlacemarkRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResultsAvailable, isDetailsVisible]);
}
